"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Process() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Sourcing heirloom dairy",
    description: "We partner with local family farms that prioritize pasture-raised cows, ensuring the richest, most flavorful cream base for our batches."
  }, {
    number: "02",
    title: "Small-batch infusion",
    description: "Each flavor profile is developed through slow, cold-steeping techniques that preserve the integrity of our organic botanicals and fruits."
  }, {
    number: "03",
    title: "Precision churning",
    description: "Our master churners monitor every rotation to achieve the signature velvet texture that defines the SCOOP experience."
  }, {
    number: "04",
    title: "Artisanal mix-ins",
    description: "We hand-fold our inclusions—from house-made honeycomb to roasted nuts—at the perfect temperature to ensure a balanced bite."
  }, {
    number: "05",
    title: "Quality sensory audit",
    description: "Every batch undergoes a rigorous sensory evaluation for texture, melt-rate, and flavor depth before it is approved for packaging."
  }, {
    number: "06",
    title: "Temperature-controlled cure",
    description: "Our ice cream is flash-frozen and cured in climate-controlled chambers to lock in freshness and prevent crystallization."
  }];
  return <section data-section-id="18843" id="from-farm-to-pint" className="bg-background text-foreground py-28 md:py-36">
      <div className="mx-auto max-w-[1240px] px-6 md:px-10 lg:px-16">
        <div ref={header.ref} className={`mx-auto flex flex-col items-center text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          <span className="font-default text-[0.8125rem] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Our craft
          </span>
          <h2 className="mt-6 font-default text-foreground" style={{
          fontSize: "clamp(2rem, 4vw, 3.25rem)",
          fontWeight: 500,
          letterSpacing: "-0.015em",
          lineHeight: 1.1,
          maxWidth: "24ch"
        }}>
            A{" "}
            <span className="italic text-primary" style={{
            fontWeight: 500
          }}>
              meticulous
            </span>{" "}
            journey from farm to pint
          </h2>
          <p className="mt-6 font-default text-foreground/80" style={{
          fontSize: "clamp(1.125rem, 1.35vw, 1.3125rem)",
          fontWeight: 400,
          letterSpacing: "-0.003em",
          lineHeight: 1.65,
          maxWidth: "52ch"
        }}>
            Every pint passes through six deliberate stages before reaching your
            freezer. We move slowly by design — because true artisanal quality 
            demands patience, precision, and a relentless pursuit of perfection.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, i) => {
          const reveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div key={step.number} data-index={i} ref={reveal.ref} className={`flex flex-col transition-all duration-700 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
            transitionDelay: `${i % 4 * 80}ms`,
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
                <span className="font-default text-foreground" style={{
              fontSize: "28px",
              fontWeight: 500,
              letterSpacing: "-0.01em",
              lineHeight: 1
            }}>
                  {step.number}
                </span>
                <h3 className="mt-4 font-default text-foreground" style={{
              fontSize: "clamp(1.05rem, 1.2vw, 1.25rem)",
              fontWeight: 600,
              letterSpacing: "-0.005em",
              lineHeight: 1.3
            }}>
                  {step.title}
                </h3>
                <p className="mt-2 font-default text-foreground/80" style={{
              fontSize: "1rem",
              fontWeight: 400,
              lineHeight: 1.7
            }}>
                  {step.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}